/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../../widgets/livewidget/nls/StringResources',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-carbon',
	'prop-types'
], function (StringResources, React, Toolkit, Carbon, PropTypes) {

	const {
		Dialog,
		FlexLayout,
		FlexItem,
		Label
	} = Toolkit;
	const { TextInput } = Carbon;

	const NUMERIC_TYPES = [
		'xsdInt',
		'xsdLong',
		'xsdShort',
		'xsdFloat',
		'xsdDecimal',
		'xsdDouble'
	];

	class TextInputPromptComponent extends React.Component {

		constructor(props) {
			super(props);

			const selectedValue = this.props.values && this.props.values[0]; // Only support single value prompts
			this.state = {
				textDir: this.props.textDir,
				type: 'text',
				inputValue: selectedValue && selectedValue.label,
				textAlign: this.props.textDir === 'RTL' ? 'right' : 'left',
				placeholder: selectedValue && selectedValue.label || StringResources.get('inputPlaceholderPrompt'),
				// Today only need to check numeric data type, but could allow date time input in future.
				validationErrorText: StringResources.get('inputInvalidText', { dataType: 'numeric' }),
				hasValidationError: false,
				onChange: this._onChange.bind(this)
			};

			this.dialogProps = {
				ariaLabel: StringResources.get('promptDialog', { promptType: 'Input' }),
				className: 'inputPrompts',
				width: '400px',
				clickaway: false
			};
		}

		_hasInvalidInput(value) {
			return value && NUMERIC_TYPES.indexOf(this.props.dataType) !== -1 && isNaN(value);
		}

		_onChange(event) {
			const value = event && event.currentTarget.value;
			const hasValidationError = !!this._hasInvalidInput(value);
			this.props.isDirty = !!value && !hasValidationError;
			this.setState({ inputValue: value, hasValidationError });
		}

		/**
		 * Stops the propagation of an event and applies the current drill through manage specification
		 * @param {object} evt
		 */
		_onDone(evt) {
			if (evt) {
				evt.stopPropagation();
			}
			const value = this.state.inputValue || this.state.placeholder;
			this.props.onDone([value]);
		}

		/**
		 * Stops the propagation of an event and removes the dialog
		 * @param {object} evt
		 */
		_onCancel(evt) {
			if (evt) {
				evt.stopPropagation();
			}
			this.props.onClose();
		}

		render() {

			const inputContent = (
				<FlexLayout direction='row'>
					<FlexItem width={'100%'}>
						<Label label={StringResources.get('inputLabelPrompt')} type='caption' className={'inputLabel'}/>
						<TextInput light {...this.state} value={this.state.inputValue} className={'inputBox'}/>
					</FlexItem>
				</FlexLayout>);

			return(
				<Dialog
					{...this.dialogProps}
					onClose={this._onCancel.bind(this)}
				>
					<FlexItem>
						<Label label={this.props.subTitle} type='caption' className={'subTitle'}/>
					</FlexItem>
					<Dialog.Header title={this.props.title}>
						{this.props.title}
					</Dialog.Header>
					<Dialog.Body>
						{inputContent}
					</Dialog.Body>
					<Dialog.Footer>
						<Dialog.Button
							primary
							id='prompt_dlg_done'
							label={StringResources.get('dlg_done')}
							title={StringResources.get('dlg_done')}
							onClick={this._onDone.bind(this)}
							disabled={!this.props.isDirty}
						/>
						<Dialog.Button
							label={StringResources.get('dlg_cancel')}
							title={StringResources.get('dlg_cancel')}
							id='prompt_dlg_cancel'
							onClick={this._onCancel.bind(this)}
						/>
					</Dialog.Footer>
				</Dialog>
			);
		}

	}

	TextInputPromptComponent.propTypes = {
		title: PropTypes.string,
		subTitle: PropTypes.string,
		onDone: PropTypes.func,
		onClose: PropTypes.func,
		values: PropTypes.array,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		dataType: PropTypes.oneOf(['xsdInt', 'xsdLong', 'xsdShort', 'xsdFloat', 'xsdDecimal', 'xsdDouble', 'xsdDate', 'xsdDateTime', 'xsdTime', 'xsdString']),
		isDirty: PropTypes.bool
	};

	return TextInputPromptComponent;
});

